import * as React from 'react'
import { useRouter } from 'next/router'
import {
  arrayify,
  reportFBViewContent,
  reportFBAddToCart,
  reportTTViewContent,
  reportTTAddToCart,
  isProduct,
  reportTTViewContentImpression,
} from '../../utils'
import { parseProduct } from './utils'
import { Product, ShopifyProductVariant } from '../../types'
import { SelectedProduct, EventType, GTagEvent } from './types'
import { useUser } from '../UserProvider'
import { useHypertune } from '../../../generated/hypertune.react'
import aa from 'search-insights'

const { useEffect } = React
const ALGOLA_INDEX_NAME = 'Storefront Search'

interface AnalyticsContextValue {
  sendCarouselClick: () => void
  sendFilterClick: () => void
  sendQuickLinkClick: () => void
  sendFormSubmit: (id: string) => void
  sendProductImpression: (
    products: SelectedProduct | SelectedProduct[],
    variant?: ShopifyProductVariant,
  ) => void
  sendProductClick: (products: SelectedProduct | SelectedProduct[]) => void
  sendProductDetailView: (products: SelectedProduct | SelectedProduct[]) => void
  sendAddToCart: (products: SelectedProduct | SelectedProduct[]) => void
  sendRemoveFromCart: (products: SelectedProduct | SelectedProduct[]) => void
  sendBeginCheckout: (
    products: SelectedProduct | SelectedProduct[],
    totalValue: number,
  ) => void
}

const AnalyticsContext = React.createContext<AnalyticsContextValue | undefined>(
  undefined,
)

export const AnalyticsConsumer = AnalyticsContext.Consumer

export const useAnalytics = () => {
  const ctx = React.useContext(AnalyticsContext)
  if (!ctx)
    throw new Error(
      'useAnalyticsContext must be used within a AnalyticsProvider',
    )
  return ctx
}

interface AnalyticsProps {
  children: React.ReactNode
}

export const AnalyticsProvider = ({ children }: AnalyticsProps) => {
  const sendEvent = (event: GTagEvent) => {
    if (typeof window === 'undefined') return
    if (window.dataLayer) window.dataLayer.push(event)
  }
  const router = useRouter()

  const { asPath } = router

  const { logCustomEvent, geoLocation, getUser } = useUser()
  const hypertune = useHypertune()

  useEffect(() => {
    aa('init', {
      appId: process.env.NEXT_PUBLIC_ALGOLIA_APP_ID || '',
      apiKey: process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_KEY || '',
      useCookie: true,
    })
  }, [])

  const sendPageView = (path: string) => {
    const search = path.match(/\?.*$/)
    if (
      /^\/products\//.test(path) &&
      (search === null || !/v=/.test(search[0]))
    ) {
      // Do not track page hits for product pages without the v= query,
      // it will be appended after loading and it would be a duplicate hit
      return
    }

    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'page_view')
    }
  }

  useEffect(() => sendPageView(asPath), [asPath])

  const sendCarouselClick: AnalyticsContextValue['sendCarouselClick'] = () => {
    sendEvent({
      event: EventType.CarouselClick,
    })
  }

  const sendFilterClick: AnalyticsContextValue['sendFilterClick'] = () => {
    sendEvent({
      event: EventType.FilterClick,
    })
  }

  const sendQuickLinkClick: AnalyticsContextValue['sendQuickLinkClick'] =
    () => {
      sendEvent({
        event: EventType.QuickLinkClick,
      })
    }

  const sendFormSubmit: AnalyticsContextValue['sendFormSubmit'] = (id) => {
    sendEvent({
      event: EventType.FormSubmit,
      formId: id,
    })
  }
  const sendProductImpression: AnalyticsContextValue['sendProductImpression'] =
    (selected, variant) => {
      const impressions = arrayify(selected).map((s, i) =>
        parseProduct(s, { position: i + 1 }),
      )

      // arrayify(selected).forEach((s) => {
      //   const selectedProduct = s?.product
      //   if (isShopifyProduct(selectedProduct)) {
      //     reportTTViewContentImpression(selectedProduct)
      //   }
      // })

      if (isProduct(selected)) {
        reportFBViewContent(selected)
        reportTTViewContentImpression(selected, variant)
      }

      sendEvent({
        event: EventType.Impressions,
        ecommerce: { impressions },
      })
    }

  const sendProductClick: AnalyticsContextValue['sendProductClick'] = (
    selected,
  ) => {
    const products = arrayify(selected).map((s, i) =>
      parseProduct(s, { position: i + 1 }),
    )

    const idsForAlgolia = products.map(
      (product) => `shopifyProduct-${product.id}`,
    )

    aa('convertedObjectIDs', {
      eventName: 'Product Clicked',
      index: ALGOLA_INDEX_NAME,
      objectIDs: idsForAlgolia,
    })

    sendEvent({
      event: EventType.ProductClick,
      ecommerce: { products },
    })
  }

  const sendProductDetailView: AnalyticsContextValue['sendProductDetailView'] =
    (selected) => {
      const products = arrayify(selected).map((s, i) =>
        parseProduct(s, { position: i + 1 }),
      )

      arrayify(selected).forEach((s) => {
        const selectedProduct = s?.product
        if (isProduct(selectedProduct)) {
          reportFBViewContent(selectedProduct)
          reportTTViewContent(selectedProduct)
        }
      })
      sendEvent({
        event: EventType.ProductDetailView,
        ecommerce: { products },
      })
    }

  const sendAddToCart: AnalyticsContextValue['sendAddToCart'] = (selected) => {
    const products = arrayify(selected).map((s, i) =>
      parseProduct(s, { position: i + 1 }),
    )

    arrayify(selected).forEach((s, i) => {
      const variant = s?.variant
      const product = parseProduct(s, { position: i + 1 })

      function hasSourceData(variant: any): variant is {
        sourceData: { priceV2: { amount: string; currencyCode: string } }
      } {
        return variant && typeof variant === 'object' && 'sourceData' in variant
      }

      const id = product && `shopifyProduct-${product.id}`

      const amount = hasSourceData(variant)
        ? variant.sourceData.priceV2.amount
        : undefined

      const currency = hasSourceData(variant)
        ? variant.sourceData?.priceV2?.currencyCode
        : undefined

      aa('addedToCartObjectIDs', {
        eventName: 'Product Added to Cart',
        index: ALGOLA_INDEX_NAME,
        objectIDs: [id],
        objectData: [
          {
            price: amount,
            quantity: 1,
          },
        ],
        value: amount?.toString(),
        currency,
      })
    })

    arrayify(selected).forEach((s) => {
      const selectedProduct = s?.product
      if (isProduct(selectedProduct)) {
        reportFBAddToCart(selectedProduct)
        reportTTAddToCart(selectedProduct)
      }
    })
    sendEvent({
      event: EventType.AddToCart,
      ecommerce: { add: { products } },
    })
  }

  const sendRemoveFromCart: AnalyticsContextValue['sendRemoveFromCart'] = (
    selected,
  ) => {
    const products = arrayify(selected).map((s, i) =>
      parseProduct(s, { position: i + 1 }),
    )
    sendEvent({
      event: EventType.RemoveFromCart,
      ecommerce: { remove: { products } },
    })
  }
  const sendBeginCheckout: AnalyticsContextValue['sendBeginCheckout'] = (
    selected,
    totalValue,
  ) => {
    const brazeProducts = arrayify(selected).map((s) =>
      s.variant ? s.variant.title : (s.product as Product)?.title,
    )
    const products = arrayify(selected).map((s, i) =>
      parseProduct(s, { position: i + 1 }),
    )

    const algoliaProducts = arrayify(selected).map((s, i) => {
      const p = parseProduct(s, { position: i + 1 })
      const objectID = `shopifyProduct-${p?.id?.split('/').pop()}`

      return {
        objectID,
        price: p?.price,
        quantity: p?.quantity,
      }
    })

    const currency = selected[0].variant.priceV2.currencyCode ?? undefined

    aa('purchasedObjectIDs', {
      eventName: 'Products Purchased',
      index: ALGOLA_INDEX_NAME,
      objectIDs: algoliaProducts.map((item) => item.objectID),
      objectData: algoliaProducts.map((item) => ({
        price: item.price ? parseFloat(item.price.replace(/,/g, '')) : 0,
        quantity: item.quantity,
      })),
      value: totalValue,
      currency,
    })

    logCustomEvent('initiate_checkout', {
      products: brazeProducts,
      path: asPath,
      value: totalValue,
      country: geoLocation?.country,
      city: geoLocation?.city,
    })
    sendEvent({
      event: EventType.BeginCheckout,
      ecommerce: { checkout: { products } },
    })
    hypertune.purchase()
  }

  const value = {
    sendCarouselClick,
    sendFilterClick,
    sendQuickLinkClick,
    sendProductImpression,
    sendProductClick,
    sendProductDetailView,
    sendAddToCart,
    sendRemoveFromCart,
    sendBeginCheckout,
    sendFormSubmit,
  }

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  )
}
